import { request } from './api';
import { toCsv } from './csv';

/** Report subscriptions, runs, inbox and notification preferences (mirrors app/shared/src/reports/types.ts). */

export type ReportScope = 'tenant' | 'platform';
export type ReportTriggerKind = 'schedule' | 'event';
export type ReportWindowKind = 'since_last' | 'day' | 'week' | 'month' | 'days' | 'yesterday' | 'last_week' | 'last_month' | 'week_to_date' | 'month_to_date';
export type ReportFormat = 'summary' | 'detailed';
export type ReportChannel = 'email' | 'in_app';
export type ReportRunTrigger = 'schedule' | 'event' | 'manual' | 'test';
export type ReportRunStatus = 'sent' | 'empty' | 'skipped' | 'failed';
export type ReportDeliveryStatus = 'sent' | 'failed' | 'muted' | 'skipped';

export interface ReportRecipients {
  roles?: string[];
  userIds?: string[];
  adminIds?: string[];
  emails?: string[];
}
export interface ReportFilters {
  jobIds?: string[];
  tenantIds?: string[];
}
export interface ReportEventConditions {
  minCount?: number;
  minRecipients?: number;
  minFailedPct?: number;
  outcomes?: string[];
  priorities?: string[];
}

export interface ReportSubscription {
  id: string;
  scope: ReportScope;
  tenantId: string | null;
  jobId: string | null;
  jobName?: string | null;
  name: string;
  description: string | null;
  enabled: boolean;
  sections: string[];
  filters: ReportFilters;
  windowKind: ReportWindowKind;
  windowDays: number | null;
  triggerKind: ReportTriggerKind;
  cron: string | null;
  cronDescription: string | null;
  timezone: string;
  eventTypes: string[];
  eventConditions: ReportEventConditions;
  cooldownMinutes: number;
  channels: ReportChannel[];
  recipients: ReportRecipients;
  format: ReportFormat;
  attachCsv: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: ReportRunStatus | null;
  lastError: string | null;
  runCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReportSubscriptionInput {
  name: string;
  description?: string | null;
  enabled?: boolean;
  jobId?: string | null;
  sections?: string[];
  filters?: ReportFilters;
  windowKind?: ReportWindowKind;
  windowDays?: number | null;
  triggerKind: ReportTriggerKind;
  cron?: string | null;
  timezone?: string;
  eventTypes?: string[];
  eventConditions?: ReportEventConditions;
  cooldownMinutes?: number;
  channels?: ReportChannel[];
  recipients?: ReportRecipients;
  format?: ReportFormat;
  attachCsv?: boolean;
}

export interface ReportStat {
  label: string;
  value: string;
  hint?: string;
}
export interface ReportTable {
  columns: string[];
  rows: Array<Array<string | number | null>>;
  numeric?: number[];
  totalRows?: number;
}
export interface ReportSection {
  key: string;
  title: string;
  description?: string;
  stats?: ReportStat[];
  table?: ReportTable;
  empty?: string;
}
export interface ReportEventItem {
  type: string;
  at: string;
  title: string;
  detail?: string;
  path?: string;
}
export interface ReportPayload {
  scope: ReportScope;
  window: { start: string; end: string; label: string; timezone: string };
  subject: { tenantName?: string; tenantSlug?: string; jobName?: string; appName: string };
  sections: ReportSection[];
  events?: { types: string[]; items: ReportEventItem[]; total: number };
  isEmpty: boolean;
}

export interface ReportRun {
  id: string;
  subscriptionId: string;
  subscriptionName?: string;
  scope: ReportScope;
  tenantId: string | null;
  jobId: string | null;
  triggeredBy: ReportRunTrigger;
  status: ReportRunStatus;
  windowStart: string;
  windowEnd: string;
  title: string;
  summaryText: string | null;
  recipientCount: number;
  emailCount: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
  payload?: ReportPayload;
}

export interface ReportDelivery {
  id: string;
  channel: ReportChannel;
  status: ReportDeliveryStatus;
  kind: 'user' | 'admin' | 'external';
  name: string;
  email: string | null;
  error: string | null;
  readAt: string | null;
  at: string;
}

export interface ReportScheduleInfo {
  valid: boolean;
  cron: string;
  timezone: string;
  description: string | null;
  nextRuns: string[];
  error: string | null;
}

export interface ReportRecipientPreview {
  kind: 'user' | 'admin' | 'external';
  id: string | null;
  name: string;
  email: string;
  channels: ReportChannel[];
  muted: boolean;
  emailOff: boolean;
  inAppOff: boolean;
}

export interface ReportDryRun {
  payload: ReportPayload;
  recipients: ReportRecipientPreview[];
  schedule: ReportScheduleInfo | null;
  window: { start: string; end: string; kind: ReportWindowKind };
}

export interface ReportInboxItem {
  deliveryId: string;
  runId: string;
  subscriptionId: string;
  title: string;
  summaryText: string | null;
  status: ReportRunStatus;
  triggeredBy: ReportRunTrigger;
  at: string;
  readAt: string | null;
}

export interface ReportCatalog {
  scope: ReportScope;
  sections: Array<{ key: string; title: string; description: string; jobLevel: boolean; adminOnly?: boolean }>;
  events: Array<{ type: string; title: string; description: string; jobLevel: boolean; conditions?: Array<keyof ReportEventConditions> }>;
  recipientRoles: Array<{ key: string; label: string; description: string; jobLevel: boolean }>;
  windows: Array<{ key: ReportWindowKind; label: string }>;
  schedulePresets: Array<{ key: string; label: string; cron: string }>;
}

export interface NotificationPreferences {
  reportEmails: boolean;
  reportInApp: boolean;
  mutedReportIds: string[];
  mutedReports: Array<{ id: string; name: string }>;
}

/** Explicit period for a one-off run or preview (ISO instants). */
export interface ReportWindowOverride {
  windowStart?: string | null;
  windowEnd?: string | null;
}

const j = <T>(path: string, body: unknown, method: 'POST' | 'PATCH' | 'PUT' = 'POST') => request<T>(path, { method, body });

/** Org-level (Super Users) or job-level (job admins) endpoints share one shape; `base` picks the scope. */
export function reportsApi(base: string) {
  return {
    catalog: () => request<{ catalog: ReportCatalog }>(`${base}/catalog`),
    /** Explain a cron + zone: validity, plain words and the next firings. */
    schedule: (cron: string, timezone: string, count = 3) => request<{ schedule: ReportScheduleInfo }>(`${base}/schedule`, { query: { cron, timezone, count } }),
    list: () => request<{ items: ReportSubscription[] }>(`${base}/subscriptions`),
    create: (body: ReportSubscriptionInput) => j<{ subscription: ReportSubscription }>(`${base}/subscriptions`, body),
    update: (id: string, body: Partial<ReportSubscriptionInput>) => j<{ subscription: ReportSubscription }>(`${base}/subscriptions/${id}`, body, 'PATCH'),
    remove: (id: string) => request<void>(`${base}/subscriptions/${id}`, { method: 'DELETE' }),
    /** Compute a draft without saving: the report, who gets it, when it fires. `existingId` keeps mutes accurate while editing. */
    dryRun: (body: ReportSubscriptionInput, existingId?: string | null) => request<ReportDryRun>(`${base}/subscriptions/dry-run`, { method: 'POST', body, query: { id: existingId ?? undefined } }),
    preview: (id: string, window?: ReportWindowOverride) => j<{ payload: ReportPayload }>(`${base}/subscriptions/${id}/preview`, window ?? {}),
    run: (id: string, opts: ReportWindowOverride & { sendEmpty?: boolean } = {}) => j<{ run: ReportRun; payload: ReportPayload }>(`${base}/subscriptions/${id}/run`, opts),
    test: (id: string) => j<{ run: ReportRun; sentTo: string }>(`${base}/subscriptions/${id}/test`, {}),
    runs: (subscriptionId?: string, limit = 50) => request<{ items: ReportRun[] }>(`${base}/runs`, { query: { subscriptionId, limit } }),
    run_: (runId: string) => request<{ run: ReportRun }>(`${base}/runs/${runId}`),
    deliveries: (runId: string) => request<{ items: ReportDelivery[] }>(`${base}/runs/${runId}/deliveries`),
  };
}
export type ReportsApi = ReturnType<typeof reportsApi>;

export const orgReportsApi = reportsApi('/api/v1/reports');

/** Headline numbers for one period (Overview KPI band). */
export interface OverviewKpis {
  messages: number;
  recipients: number;
  attempted: number;
  delivered: number;
  failed: number;
  contactsAdded: number;
  optOuts: number;
  optIns: number;
  keywordHits: number;
  joinsCompleted: number;
  automationRuns: number;
  automationFailed: number;
  signIns: number;
  failedSignIns: number;
  supportFiled: number;
}
export interface OverviewPoint {
  day: string;
  messages: number;
  recipients: number;
  delivered: number;
  failed: number;
  contactsAdded: number;
  optOuts: number;
  keywordHits: number;
}
export interface OverviewResponse {
  payload: ReportPayload;
  days: number;
  range: { from: string; to: string; custom: boolean; timezone: string };
  previous: { from: string; to: string };
  kpis: { current: OverviewKpis; previous: OverviewKpis };
  series: OverviewPoint[];
}
/** Organization → Reports → Overview: sections, KPIs vs the previous period and a daily series, for the last N days or an explicit period. */
export const orgReportsOverview = (opts: { days?: number; from?: string; to?: string }) => request<OverviewResponse>('/api/v1/reports/overview', { query: opts });
export const jobReportsApi = (jobId: string) => reportsApi(`/api/v1/jobs/${jobId}/reports`);

export const inboxApi = {
  list: () => request<{ items: ReportInboxItem[]; unread: number }>('/api/v1/reports/inbox'),
  read: (deliveryIds?: string[]) => j<{ marked: number }>('/api/v1/reports/inbox/read', { deliveryIds }),
};

export const notificationPrefsApi = {
  get: () => request<NotificationPreferences>('/api/v1/auth/me/notifications'),
  update: (body: Partial<Pick<NotificationPreferences, 'reportEmails' | 'reportInApp' | 'mutedReportIds'>>) => j<NotificationPreferences>('/api/v1/auth/me/notifications', body, 'PATCH'),
};

export const TRIGGER_LABEL: Record<ReportTriggerKind, string> = { schedule: 'On a schedule', event: 'When something happens' };
export const RUN_STATUS_LABEL: Record<ReportRunStatus, string> = { sent: 'Sent', empty: 'Quiet window', skipped: 'No recipients', failed: 'Failed' };
export const RUN_TRIGGER_LABEL: Record<ReportRunTrigger, string> = { schedule: 'Scheduled', event: 'Triggered', manual: 'Run by hand', test: 'Test' };
export const DELIVERY_STATUS_LABEL: Record<ReportDeliveryStatus, string> = { sent: 'Delivered', failed: 'Failed', muted: 'Muted', skipped: 'Skipped' };
export const CHANNEL_LABEL: Record<ReportChannel, string> = { email: 'Email', in_app: 'In-app' };
export const ROLE_LABEL: Record<string, string> = { org_admin: 'Super Users', job_admins: 'Job admins', job_users: 'Everyone on the job', all_users: 'Everyone in the organization', all_admins: 'Every operator' };

export const WINDOW_LABEL: Record<ReportWindowKind, string> = {
  since_last: 'Since the last run',
  day: 'Last 24 hours',
  yesterday: 'Yesterday',
  week: 'Last 7 days',
  last_week: 'Last week (Mon–Sun)',
  week_to_date: 'This week so far',
  month: 'Last 30 days',
  last_month: 'Last calendar month',
  month_to_date: 'This month so far',
  days: 'Last N days',
};
export const WINDOW_HINT: Record<ReportWindowKind, string> = {
  since_last: 'Everything since the previous run, so nothing is counted twice. The first run covers 24 hours.',
  day: 'A rolling 24 hours ending when the report runs.',
  yesterday: 'The previous calendar day, midnight to midnight in the report’s time zone.',
  week: 'A rolling 7 days ending when the report runs.',
  last_week: 'The previous Monday-to-Sunday week. Pairs well with a Monday morning schedule.',
  week_to_date: 'From Monday 00:00 up to the moment the report runs.',
  month: 'A rolling 30 days ending when the report runs.',
  last_month: 'The previous calendar month, 1st to last day. Pairs well with a 1st-of-month schedule.',
  month_to_date: 'From the 1st at 00:00 up to the moment the report runs.',
  days: 'A rolling number of days you choose.',
};

export function describeWindow(kind: ReportWindowKind, days?: number | null): string {
  return kind === 'days' ? `Last ${days ?? 7} days` : WINDOW_LABEL[kind];
}

export function describeTrigger(s: ReportSubscription): string {
  if (s.triggerKind === 'schedule') return `${s.cronDescription ?? s.cron ?? 'Schedule'} · ${s.timezone}`;
  const n = s.eventTypes.length;
  return `${n} ${n === 1 ? 'event' : 'events'} watched${s.cooldownMinutes ? ` · at most every ${describeMinutes(s.cooldownMinutes)}` : ''}`;
}

export function describeMinutes(minutes: number): string {
  if (minutes % 1440 === 0) return `${minutes / 1440} ${minutes / 1440 === 1 ? 'day' : 'days'}`;
  if (minutes % 60 === 0) return `${minutes / 60} ${minutes / 60 === 1 ? 'hour' : 'hours'}`;
  return `${minutes} min`;
}

export function audienceSummary(rec: ReportRecipients, opts: { peopleWord?: string } = {}): string {
  const parts: string[] = [];
  for (const r of rec.roles ?? []) parts.push(ROLE_LABEL[r] ?? r);
  const n = (rec.userIds?.length ?? 0) + (rec.adminIds?.length ?? 0);
  if (n) parts.push(`${n} ${n === 1 ? (opts.peopleWord ?? 'person') : 'people'}`);
  if (rec.emails?.length) parts.push(`${rec.emails.length} ${rec.emails.length === 1 ? 'address' : 'addresses'}`);
  return parts.join(', ') || 'Nobody yet';
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Every stat and table of a computed report in one CSV, section by section (same layout as the email attachment). */
export function payloadToCsv(payload: ReportPayload, reportName: string): { filename: string; csv: string } {
  const rows: Array<Array<string | number | null>> = [];
  rows.push(['Report', reportName]);
  rows.push(['Window', `${payload.window.label} (${payload.window.timezone})`]);
  rows.push([]);
  if (payload.events && payload.events.items.length > 0) {
    rows.push(['Events']);
    rows.push(['When', 'Type', 'Title', 'Detail']);
    for (const e of payload.events.items) rows.push([e.at, e.type, e.title, e.detail ?? '']);
    rows.push([]);
  }
  for (const s of payload.sections) {
    rows.push([s.title]);
    for (const st of s.stats ?? []) rows.push([st.label, st.value, st.hint ?? '']);
    if (s.table && s.table.rows.length > 0) {
      rows.push([...s.table.columns]);
      for (const r of s.table.rows) rows.push([...r]);
    }
    rows.push([]);
  }
  const safe = reportName.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'report';
  const [head = [], ...rest] = rows;
  return { filename: `${safe}-${payload.window.end.slice(0, 10)}.csv`, csv: toCsv(head.map((v) => (v === null ? '' : String(v))), rest) };
}
